import { Box, Typography } from "@outschool/backpack";
import { EventNames } from "@outschool/data-schemas";
import {
  CurrencyCode,
  EnrollmentInput,
} from "@outschool/gql-backend-generated";
import {
  ActivityEnrollQueryQuery,
  ActivityEnrollableTimesModal_ActivityFragment,
  CharterSchoolFragmentFragment,
  CreateEnrollmentMeetingPurchaseMutation,
  CreateEnrollmentMeetingPurchaseMutationVariables,
  CurrentUserPaymentMethodsQuery,
  EnrollmentCharterParentInput,
  EnrollmentMeetingPurchaseQueryQuery,
  FullCurrentUserFragmentFragment,
  GetLearnerItems_EnrollmentFragment,
  ParentChildrenQuery,
  PricingBreakdownQueryQuery,
  PurchaseRecurringMeetingsInfo,
  SettleOrderEnrollmentMeetingPurchaseMutation,
  ValidateMeetingRequestsForPurchaseMutation,
  ValidateMeetingRequestsForPurchaseMutationVariables,
} from "@outschool/gql-frontend-generated";
import { ValidateMeetingRequestsForPurchaseError } from "@outschool/gql-frontend-generated/src";
import {
  USD_STATIC_EXCHANGE_RATE,
  useCurrencyLocalization,
  useTranslation,
} from "@outschool/localization";
import { dayjs, startOfIsoWeekUtc } from "@outschool/time";
import {
  IntegrationCategory,
  useAnalytics,
  useCheckoutStarted,
  useTrackEvent,
  useTrackEventWithSchema,
} from "@outschool/ui-analytics";
import { gql, useMutation } from "@outschool/ui-apollo";
import {
  EsaSessionType,
  esaCheckoutUrl,
  useFundingProgramAbbr,
  useSession,
} from "@outschool/ui-auth";
import {
  useEsaAvailability,
  useTutorMatchContext,
} from "@outschool/ui-components-website";
import { useNavigation } from "@outschool/ui-utils";
import _ from "lodash";
import React from "react";
import { useLocation } from "react-router";

import * as EnrollmentPayment from "../../../shared/EnrollmentPayment";
import {
  CLASSWALLET_CALLBACK_URL,
  CLASSWALLET_VENDOR_ID,
} from "../../../shared/Env";
import ActionType from "../../actions/ActionType";
import { useHasEnrolled, usePaymentMethods } from "../../hooks/CurrentUser";
import { useTrackLearnerCreatedDuringEnrollment } from "../../hooks/useTrackLearnerCreatedDuringEnrollment";
import { shouldChargeInLocalCurrency } from "../../lib/Localization";
import {
  useCreditAccount,
  useEsaMembershipCreditAccount,
  useSubscriptionCreditAccount,
} from "../../queries/CreditQueries";
import { useUpdateCurrentUserMutation } from "../../queries/CurrentUser";
import { createEnrollmentMeetingPurchaseMutation } from "../../queries/EnrollmentMutations";
import { useExchangeRate } from "../../queries/ExchangeRateQueries";
import {
  useAcceptPaymentWithClassWallet,
  useAcceptPaymentWithCoupon,
  useAcceptPaymentWithPricingOffer,
  useAcceptPaymentWithPurchaseOrder,
  useCancelOrder,
  useSettleOrderEnrollmentMeetingPurchase,
} from "../../queries/PaymentMutations";
import { PrepayType } from "../../routes/EnrollmentMeetingPurchasePage";
import RequestAgeRangeException from "../account/RequestAgeRangeException";
import { SavePaymentMethod } from "../account/SavePaymentMethod";
import LoadingWithMessage from "../LoadingWithMessage";
import { StripeCheckoutForm } from "../payments/StripeCheckoutForm";
import ActivitySummary from "./ActivitySummary";
import CharterPurchaseComplete from "./CharterPurchaseComplete";
import { shouldShowCoppa } from "./CoppaConsent";
import { defaultLearnerItem } from "./enrollmentForm/learnerItems/getLearnerItems";
import EnrollmentMeetingPurchaseDuration from "./EnrollmentMeetingPurchaseDuration";
import EnrollmentMeetingPurchaseForm from "./EnrollmentMeetingPurchaseForm";
import EnrollmentMeetingPurchaseFutureMeetings, {
  EnrollmentMeetingPurchaseFutureMeetingsWrapper,
} from "./EnrollmentMeetingPurchaseFutureMeetings";
import EnrollmentMeetingPurchasePrepayDuration from "./EnrollmentMeetingPurchasePrepayDuration";
import { EnrollmentMeetingsWithoutPayment } from "./EnrollmentMeetingsWithoutPayment";
import HowDidYouHearAboutUsModal from "./HowDidYouHearAboutUsModal";
import MultipleMeetingsModal from "./MultipleMeetingsModal";
import { OrderProvider, useOrderContext } from "./OrderContext";
import { ScheduleDateTimeSelector } from "./ScheduleDateTimeSelector";
import { EnrollmentOrgTeacher, FutureMeetingInfo } from "./types";

export const validateMeetingRequestsForPurchaseMutation = gql`
  mutation ValidateMeetingRequestsForPurchase(
    $requestUids: [String!]!
    $activityUid: String!
    $enrollmentMeetingPurchaseUid: String!
    $sectionUid: String
  ) {
    validateMeetingRequestsForPurchase(
      requestUids: $requestUids
      activityUid: $activityUid
      enrollmentMeetingPurchaseUid: $enrollmentMeetingPurchaseUid
      sectionUid: $sectionUid
    ) {
      isValid
      error
    }
  }
`;

export function useValidateMeetingRequestsForPurchase(): (
  args: ValidateMeetingRequestsForPurchaseMutationVariables,
) => Promise<
  ValidateMeetingRequestsForPurchaseMutation["validateMeetingRequestsForPurchase"]
> {
  const [validateMeetingRequestsForPurchase] = useMutation<
    ValidateMeetingRequestsForPurchaseMutation,
    ValidateMeetingRequestsForPurchaseMutationVariables
  >(validateMeetingRequestsForPurchaseMutation);

  const validateFn = React.useCallback(
    async (args: ValidateMeetingRequestsForPurchaseMutationVariables) => {
      const { data } = await validateMeetingRequestsForPurchase({
        variables: args,
      });
      return (
        data?.validateMeetingRequestsForPurchase ?? {
          isValid: true,
          error: null,
        }
      );
    },
    [validateMeetingRequestsForPurchase],
  );

  return validateFn;
}

export type EnrollmentMeetingPurchaseQueryType = NonNullable<
  EnrollmentMeetingPurchaseQueryQuery["enrollmentMeetingPurchaseForm"]
>;

export type EnrollmentFromStripePurchaseOrder =
  SettleOrderEnrollmentMeetingPurchaseMutation["settleOrderEnrollmentMeetingPurchase"]["enrollments"][number];

export type EnrollmentMeetingPurchaseWithStripeProps = {
  canPayLater: EnrollmentMeetingPurchaseQueryType["canPayLater"];
  // @ts-ignore TS(2339): Property 'activity' does not exist on type '{ __ty... Remove this comment to see the full error message
  activity: EnrollmentMeetingPurchaseQueryQuery["enrollmentMeetingPurchaseForm"]["activity"];
  // @ts-ignore TS(2339): Property 'learner' does not exist on type '{ __typ... Remove this comment to see the full error message
  learner?: EnrollmentMeetingPurchaseQueryQuery["enrollmentMeetingPurchaseForm"]["learner"];
  userTimeZone: string;
  payingUser: FullCurrentUserFragmentFragment;
  onPaymentComplete: ({
    enrollment,
    enrollmentMeetingPurchase,
    isFirstPurchaseByUser,
    orderSlugId,
    enrollmentDuration,
    shouldCheckForSavedPayment,
  }: {
    enrollment?: EnrollmentFromStripePurchaseOrder;
    enrollmentMeetingPurchase:
      | SettleOrderEnrollmentMeetingPurchaseMutation["settleOrderEnrollmentMeetingPurchase"]["enrollmentMeetingPurchase"]
      | CreateEnrollmentMeetingPurchaseMutation["createEnrollmentMeetingPurchase"]["enrollmentMeetingPurchase"];
    orderSlugId: string;
    isFirstPurchaseByUser: boolean;
    enrollmentDuration?: string; // needs to be a string for tracking
    shouldCheckForSavedPayment?: boolean;
  }) => void;
  onCompleteFlow: ({
    enrollment,
    shouldCheckForSavedPayment,
  }: {
    enrollment?: EnrollmentFromStripePurchaseOrder;
    shouldCheckForSavedPayment?: boolean;
  }) => void;
  startTime: Date;
  endTime: Date;
  givenDuration?: number | null;
  onClose: (e?: React.SyntheticEvent, resetFields?: boolean) => void;
  refetchMeetings?: () => void;
  enrollingParent: EnrollmentMeetingPurchaseQueryQuery["currentUser"];
  meetingRequestUids?: string[];
  // @ts-ignore TS(2339): Property 'meetingRequests' does not exist on type '{ __ty... Remove this comment to see the full error message
  meetingRequests?: EnrollmentMeetingPurchaseQueryQuery["enrollmentMeetingPurchaseForm"]["meetingRequests"];
  conflictingEnrollments: GetLearnerItems_EnrollmentFragment[];
  pendingEnrollments: GetLearnerItems_EnrollmentFragment[];
  enrollmentsInOtherSections?: GetLearnerItems_EnrollmentFragment[];
  onMultipleMeetingsModalClose: () => void;
  actualWeeklyMeetings: number;
  orgTeacher?: EnrollmentOrgTeacher;
  showMultipleMeetingsModal: boolean;
  showCharterPurchaseComplete: boolean;
  setParentByEmail: React.Dispatch<
    React.SetStateAction<ParentChildrenQuery["parentByEmail"] | null>
  >;
  prepayType?: PrepayType | null;
  recurringScheduleUid?: string;
  sectionUid?: string;
  showCloseButton?: boolean;
  showCancel?: boolean;
  resetFields?: boolean;
  activityWithEnrollableTimes?: ActivityEnrollableTimesModal_ActivityFragment;
  isThreeStepBooking?: boolean;
};

export type EnrollmentMeetingPurchaseCharterInput =
  EnrollmentCharterParentInput & {
    charterSchool?: CharterSchoolFragmentFragment;
    intercomUrl?: string;
    purchaseOrderId?: string;
    isValid: boolean;
  };

enum Step {
  ThreeStepBookingAvailability = "threeStepBookingAvailability",
  ThreeStepEnrollmentMeetingPurchaseForm = "threeStepEnrollmentMeetingPurchaseForm",
  EnrollmentMeetingPurchaseForm = "enrollmentMeetingPurchaseForm",
  MeetingCountSelected = "meetingCountSelected",
  FutureMeetings = "futureMeetings",
  ShouldCreateEnrollmentMeetingPurchase = "ShouldCreateEnrollmentMeetingPurchase",
  RequestingAgeRangeException = "requestingAgeRangeException",
  CreatingEnrollmentMeetingPurchase = "creatingEnrollmentMeetingPurchase",
  PayingForFreeMeeting = "payingForFreeMeeting",
  PurchaseOrderPayment = "purchaseOrderPayment",
  PaymentForm = "paymentForm",
  SavePaymentForm = "savePaymentForm",
  PurchaseCompletePaidByUser = "purchaseCompletePaidByUser", // paid using stripe
  PurchaseCompletePaidByOther = "purchaseCompletePaidByOther", // free class, purchase order, etc.
  HandlingPayment = "handlingPayment",
  HandlingFreePayment = "handlingFreePayment",
  SuggestMultipleMeetings = "suggestMultipleMeetings",
  ShowCharterPurchaseComplete = "showCharterPurchaseComplete",
  CreateMeetingsWithoutPayment = "createMeetingsWithoutPayment",
  ClassWalletPayment = "classWalletPayment",
  HowDidYouHearAboutUs = "howDidYouHearAboutUs",
  FlowComplete = "flowComplete",
  PayingForEnrollment = "PayingForEnrollment",
}

// @ts-ignore TS(2339): Property 'children' does not exist on type '{ __ty... Remove this comment to see the full error message
type Child = ActivityEnrollQueryQuery["currentUser"]["children"][number];

interface BaseState {
  step: Step;
  error: string;
  needsCoppaConfirmation: boolean;
  paymentIntent: string | null;
  orderSlugId: string;
  selectedLearnerForMessage: Child | null;
  postPaymentCalled: boolean;
  isFirstPurchaseByUser: boolean;
  prepayType: PrepayType | null;
  enrollmentCharterInput: EnrollmentMeetingPurchaseCharterInput | null;
  enrollmentDuration: number | null;
}

interface CreatingState extends BaseState {
  step:
    | Step.ThreeStepBookingAvailability
    | Step.ThreeStepEnrollmentMeetingPurchaseForm
    | Step.EnrollmentMeetingPurchaseForm
    | Step.ShouldCreateEnrollmentMeetingPurchase
    | Step.CreatingEnrollmentMeetingPurchase
    | Step.RequestingAgeRangeException
    | Step.PaymentForm
    | Step.SavePaymentForm
    | Step.ClassWalletPayment
    | Step.HandlingPayment
    | Step.HandlingFreePayment
    | Step.CreateMeetingsWithoutPayment
    | Step.PayingForEnrollment;
  enrollmentMeetingPurchase: CreateEnrollmentMeetingPurchaseMutation["createEnrollmentMeetingPurchase"]["enrollmentMeetingPurchase"];
}

type CreatedOrSettledMeetingPurchase =
  | CreateEnrollmentMeetingPurchaseMutation["createEnrollmentMeetingPurchase"]["enrollmentMeetingPurchase"]
  | SettleOrderEnrollmentMeetingPurchaseMutation["settleOrderEnrollmentMeetingPurchase"]["enrollmentMeetingPurchase"];

interface PaidState extends BaseState {
  step:
    | Step.PurchaseCompletePaidByUser
    | Step.SuggestMultipleMeetings
    | Step.HowDidYouHearAboutUs
    | Step.PurchaseCompletePaidByOther
    | Step.PayingForFreeMeeting
    | Step.PurchaseOrderPayment
    | Step.ShowCharterPurchaseComplete
    | Step.FlowComplete;
  /**
   * enrollment will only exist when the user has paid for the meeting with a settlement order
   * otherwise it is alright if enrollment is undefined or null as the receiving code handles nullability
   */
  enrollment?: EnrollmentFromStripePurchaseOrder;
  enrollmentMeetingPurchase: CreatedOrSettledMeetingPurchase;
}

interface InitialState extends BaseState {
  step:
    | Step.MeetingCountSelected
    | Step.FutureMeetings
    | Step.ThreeStepBookingAvailability
    | Step.EnrollmentMeetingPurchaseForm;
}

type State = CreatingState | PaidState | InitialState;

export const MAX_DAYS_AVAILABILITY = 90;

type Action =
  | {
      type: "CANCELLED";
      value?: {
        isThreeStepBooking: boolean;
      };
    }
  | {
      type: "GO_BACK";
      value?: {
        isThreeStepBooking: boolean;
      };
    }
  | { type: "HANDLING_PAYMENT" }
  | { type: "HANDLING_FREE_PAYMENT" }
  | { type: "CREATING_ENROLLMENT_MEETING_PURCHASE" }
  | { type: "REQUESTING_AGE_RANGE_EXCEPTION"; value: { learner: Child } }
  | { type: "ERRORED"; error?: string }
  | {
      type: "ENROLL_CLICK";
      value: {
        shouldShowCoppa: boolean;
        prepayType: PrepayType | null;
        enrollmentCharterInput: EnrollmentMeetingPurchaseCharterInput | null;
        canPayLater: boolean;
        sectionUid?: string;
      };
    }
  | { type: "POST_PAYMENT_CALLED" }
  | { type: "COPPA_CONFIRMED"; value: { canPayLater: boolean } }
  | {
      type: "MEETING_COUNT_SELECTED";
      value: {
        shouldShowFutureMeetings: boolean;
      };
    }
  | { type: "FUTURE_MEETINGS_SHOWN" }
  | {
      type: "ENROLLMENT_MEETING_PURCHASE_CREATED";
      value: {
        enrollmentMeetingPurchase: CreateEnrollmentMeetingPurchaseMutation["createEnrollmentMeetingPurchase"]["enrollmentMeetingPurchase"];
        orderSlugId: string;
        stripeCents: number;
        subscriptionOutstandingBalanceCredits: number;
        pricingOffer?: PricingBreakdownQueryQuery["pricingBreakdown"]["pricingOffer"];
        // @ts-ignore TS(2339): Property 'paymentMethods' does not exist on type '... Remove this comment to see the full error message
        paymentMethods: CurrentUserPaymentMethodsQuery["currentUser"]["paymentMethods"];
        hasActiveSubscription: boolean;
        canPayLater: boolean;
        enrollmentDuration: number | null;
      };
    }
  | {
      type: "PAYMENT_BY_USER_COMPLETE";
      value: {
        enrollment: EnrollmentFromStripePurchaseOrder;
        enrollmentMeetingPurchase: CreatedOrSettledMeetingPurchase;
        isFirstPurchaseByUser: boolean;
      };
    }
  | {
      type: "HOW_DID_YOU_HEAR_ABOUT_US";
      value: {
        enrollment?: EnrollmentFromStripePurchaseOrder;
        enrollmentMeetingPurchase: CreatedOrSettledMeetingPurchase;
        isFirstPurchaseByUser: boolean;
      };
    }
  | {
      type: "ON_COMPLETE_FLOW";
      value: {
        enrollment?: EnrollmentFromStripePurchaseOrder;
        enrollmentMeetingPurchase: CreatedOrSettledMeetingPurchase;
        isFirstPurchaseByUser: boolean;
      };
    }
  | {
      type: "SHOW_SUGGESTED_WEEKLY_MEETINGS";
      value: {
        enrollmentMeetingPurchase: CreatedOrSettledMeetingPurchase;
      };
    }
  | {
      type: "SHOW_CHARTER_PURCHASE_COMPLETE";
      value: {
        enrollmentMeetingPurchase: CreatedOrSettledMeetingPurchase;
      };
    };

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "CANCELLED":
      if (action.value?.isThreeStepBooking) {
        return { ...initialState, step: Step.ThreeStepBookingAvailability };
      }
      return { ...initialState };
    case "GO_BACK":
      return {
        ...state,
        step: action.value?.isThreeStepBooking
          ? Step.ThreeStepBookingAvailability
          : Step.FutureMeetings,
      };
    case "ERRORED":
      return {
        ...state,
        step: Step.EnrollmentMeetingPurchaseForm,
        // @ts-ignore TS(2322): Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
        error: action.error,
      };
    case "MEETING_COUNT_SELECTED":
      // @ts-ignore TS(2322): Type '{ step: Step.EnrollmentMeetingPurchaseForm |... Remove this comment to see the full error message
      return {
        ...state,
        step: action.value.shouldShowFutureMeetings
          ? Step.FutureMeetings
          : Step.EnrollmentMeetingPurchaseForm,
      };
    case "FUTURE_MEETINGS_SHOWN":
      // @ts-ignore TS(2322): Type '{ step: Step.EnrollmentMeetingPurchaseForm; ... Remove this comment to see the full error message
      return {
        ...state,
        step: Step.EnrollmentMeetingPurchaseForm,
      };
    case "ENROLL_CLICK": {
      const nextStep = (): CreatingState["step"] => {
        return action.value.canPayLater
          ? Step.CreateMeetingsWithoutPayment
          : Step.ShouldCreateEnrollmentMeetingPurchase;
      };

      // @ts-ignore TS(2322): Type '{ Step.ShouldCreateEn... Remove this comment to see the full error message
      return {
        ...state,
        needsCoppaConfirmation: action.value.shouldShowCoppa,
        enrollmentCharterInput: action.value.enrollmentCharterInput,
        prepayType: action.value.prepayType,
        step: nextStep(),
      };
    }
    case "CREATING_ENROLLMENT_MEETING_PURCHASE":
      // @ts-ignore TS(2322): Type '{ step: Step.CreatingEnrollmentMeetingPurcha... Remove this comment to see the full error message
      return { ...state, step: Step.CreatingEnrollmentMeetingPurchase };
    case "REQUESTING_AGE_RANGE_EXCEPTION":
      // @ts-ignore TS(2322): Type '{ step: Step.RequestingAgeRangeException; se... Remove this comment to see the full error message
      return {
        ...state,
        step: Step.RequestingAgeRangeException,
        selectedLearnerForMessage: action.value.learner,
      };
    case "ENROLLMENT_MEETING_PURCHASE_CREATED": {
      return {
        ...state,
        ...action.value,
        step: !!action.value.enrollmentMeetingPurchase.confirmedAt
          ? Step.PurchaseCompletePaidByOther
          : action.value.hasActiveSubscription &&
            action.value.stripeCents === 0 &&
            action.value.subscriptionOutstandingBalanceCredits === 0
          ? Step.PayingForEnrollment
          : action.value.hasActiveSubscription &&
            action.value.subscriptionOutstandingBalanceCredits > 0
          ? Step.PaymentForm
          : requiresSavedPaymentForm(state, action)
          ? Step.SavePaymentForm
          : action.value.stripeCents === 0
          ? Step.PayingForFreeMeeting
          : state.prepayType === PrepayType.CharterPO
          ? Step.PurchaseOrderPayment
          : state.prepayType === PrepayType.ClassWallet
          ? Step.ClassWalletPayment
          : Step.PaymentForm,
      };
    }
    case "HANDLING_PAYMENT":
      // @ts-ignore TS(2322): Type '{ step: Step.HandlingPayment; enrollment: { ... Remove this comment to see the full error message
      return { ...state, step: Step.HandlingPayment };
    case "HANDLING_FREE_PAYMENT":
      // @ts-ignore TS(2322): Type '{ step: Step.HandlingFreePayment; enrollment... Remove this comment to see the full error message
      return { ...state, step: Step.HandlingFreePayment };
    case "PAYMENT_BY_USER_COMPLETE":
      return {
        ...state,
        ...action.value,
        step: Step.PurchaseCompletePaidByUser,
      };
    case "POST_PAYMENT_CALLED":
      return {
        ...state,
        postPaymentCalled: true,
      };
    case "SHOW_SUGGESTED_WEEKLY_MEETINGS":
      return {
        ...state,
        ...action.value,
        step: Step.SuggestMultipleMeetings,
      };
    case "SHOW_CHARTER_PURCHASE_COMPLETE":
      return {
        ...state,
        ...action.value,
        step: Step.ShowCharterPurchaseComplete,
      };
    case "HOW_DID_YOU_HEAR_ABOUT_US":
      return {
        ...state,
        ...action.value,
        step: Step.HowDidYouHearAboutUs,
      };
    default:
      return state;
  }
}

const initialState: InitialState = {
  error: "",
  step: Step.MeetingCountSelected,
  needsCoppaConfirmation: false,
  orderSlugId: "",
  paymentIntent: null,
  selectedLearnerForMessage: null,
  postPaymentCalled: false,
  isFirstPurchaseByUser: false,
  prepayType: null,
  enrollmentCharterInput: null,
  enrollmentDuration: 1,
};

function requiresSavedPaymentForm(state: State, action: Action) {
  if (action.type !== "ENROLLMENT_MEETING_PURCHASE_CREATED") {
    throw new Error("Invalid action type: " + action.type);
  }
  const { stripeCents, pricingOffer, paymentMethods, hasActiveSubscription } =
    action.value;

  if (hasActiveSubscription) {
    return false;
  }

  if (stripeCents > 0) {
    return false;
  }

  if (pricingOffer && !pricingOffer.requirePaymentMethod) {
    return false;
  }

  return state.needsCoppaConfirmation && paymentMethods.length === 0;
}

function EnrollmentMeetingPurchaseWithStripe({
  canPayLater,
  activity,
  learner,
  payingUser,
  onPaymentComplete = ({}) => {},
  onCompleteFlow = ({}) => {},
  onClose,
  startTime,
  endTime,
  userTimeZone,
  meetingRequestUids,
  meetingRequests,
  onMultipleMeetingsModalClose,
  actualWeeklyMeetings,
  orgTeacher,
  showMultipleMeetingsModal,
  showCharterPurchaseComplete,
  prepayType = null,
  setParentByEmail,
  recurringScheduleUid,
  refetchMeetings,
  sectionUid,
  showCloseButton = true,
  showCancel = false,
  resetFields = false,
  givenDuration,
  activityWithEnrollableTimes,
  isThreeStepBooking = false,
  ...otherProps
}: EnrollmentMeetingPurchaseWithStripeProps) {
  const analytics = useAnalytics();
  const { t } = useTranslation(
    "client\\components\\enrolling\\EnrollmentMeetingPurchaseWithStripe",
  );

  const trackWithSchema = useTrackEventWithSchema(
    IntegrationCategory.Advertising,
  );

  const { enrollingParent = undefined } = otherProps;
  const { isMeetingBeyondEsaEnd } = useEsaAvailability(activity);
  const { esaSessionData, isEsaSession, esaSessionType } = useSession();
  const esaState = useFundingProgramAbbr();
  const isClassWalletSession = esaSessionType === EsaSessionType.CW;
  const location = useLocation();
  const { currentUser } = useSession();
  const { tutorMatchEnrollmentData, setTutorMatchEnrollmentData } =
    useTutorMatchContext();
  const hasActiveSubscription = !!currentUser?.canPayWithSubscriptionCredits;
  const hasEsaMembership = !!currentUser?.canPayWithSubscriptionCredits;
  const isPrepayEnrollment = !!prepayType;
  // @ts-ignore TS(2532): Object is possibly 'undefined'.
  const isAcceptingMeetingRequests = meetingRequestUids?.length > 0;

  const makeItOngoing =
    !isEsaSession && (recurringScheduleUid || recurringScheduleUid === "");

  const [enrollmentDuration, setEnrollmentDuration] = React.useState(
    givenDuration === undefined ? 1 : givenDuration,
  );

  const initialStateOverride: InitialState = isThreeStepBooking
    ? {
        ...initialState,
        step:
          givenDuration === undefined
            ? Step.ThreeStepBookingAvailability
            : Step.EnrollmentMeetingPurchaseForm,
      }
    : makeItOngoing
    ? { ...initialState, step: Step.FutureMeetings }
    : initialState;

  const [state, dispatch] = React.useReducer(reducer, initialStateOverride);
  const [createEnrollmentMeeting, { loading: creationInProgress }] =
    useMutation<
      CreateEnrollmentMeetingPurchaseMutation,
      CreateEnrollmentMeetingPurchaseMutationVariables
    >(createEnrollmentMeetingPurchaseMutation);
  const validateMeetingRequestsForPurchase =
    useValidateMeetingRequestsForPurchase();
  const { paymentMethods, loading: arePaymentMethodsLoading } =
    usePaymentMethods();
  const { loading: isCreditAccountLoading, acceptPaymentWithStoredCredit } =
    useCreditAccount();
  const {
    loading: isSubscriptionAccountLoading,
    acceptPaymentWithSubscriptionCredit,
  } = useSubscriptionCreditAccount();
  const {
    loading: isEsaMembershipAccountLoading,
    acceptPaymentWithEsaMembershipCredit,
  } = useEsaMembershipCreditAccount();
  const trackEvent = useTrackEvent();
  const trackCheckoutStarted = useCheckoutStarted();

  const [futureMeetingAvailability, setFutureMeetingAvailability] =
    React.useState<FutureMeetingInfo[]>([]);

  /**
   * futureMeetingAvailability makes assumptions to the availability of time
   * slots beyond 90 days out. For prepay meetings only count on meeting times
   * we know to be available.
   */
  const [prepayMeetingAvailability, setPrepayMeetingAvailability] =
    React.useState<FutureMeetingInfo[]>([]);
  const [isContinuousSchedulingAvailable, setIsContinuousSchedulingAvailable] =
    React.useState(false);
  const [nextBillingDate, setNextBillingDate] = React.useState<Date>();
  const [updateCurrentUser] = useUpdateCurrentUserMutation();
  const settleOrder = useSettleOrderEnrollmentMeetingPurchase();
  const cancelOrder = useCancelOrder();
  const { organizationAdmin } = payingUser || {};
  const isCharterEnrollment =
    prepayType === PrepayType.CharterPO ||
    prepayType === PrepayType.CharterAdmin;

  // MARK: Order context
  const {
    enrollmentInputs,
    promoCode,
    currencyCode,
    exchangeRate,
    pricingBreakdown,
    refetchPricingBreakdown,
    isPricingBreakdownLoading,
    setEnrollmentInputs,
    learnerItems,
    setLearnerItems,
    setOverrideLearnerItems,
    setOngoingStartTime,
    setOngoingEndTime,
    setTeacherUid,
    setPrepayDurationWeeks,
    setCharterAdminEnrollmentInput,
  } = useOrderContext();

  const pricingOffer = pricingBreakdown?.pricingOffer;

  const acceptPaymentWithPurchaseOrder = useAcceptPaymentWithPurchaseOrder();
  const acceptPaymentWithClassWallet = useAcceptPaymentWithClassWallet();
  const acceptPaymentWithPricingOffer = useAcceptPaymentWithPricingOffer({
    orderSlugId: state.orderSlugId,
  });
  const navigate = useNavigation();

  const isChargingInLocalCurrency = shouldChargeInLocalCurrency(currencyCode);
  const currencyCodeToWrite = isChargingInLocalCurrency
    ? currencyCode
    : CurrencyCode.Usd;
  const exchangeRateToWrite = isChargingInLocalCurrency
    ? exchangeRate
    : USD_STATIC_EXCHANGE_RATE;

  const acceptPaymentWithCoupon = useAcceptPaymentWithCoupon({
    orderSlugId: state.orderSlugId,
    activityUid: activity.uid,
    amountCents: pricingBreakdown?.couponCents ?? 0,
    // @ts-ignore TS(2322): Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
    code: promoCode,
    currencyCode: currencyCodeToWrite,
    exchangeRate: exchangeRateToWrite,
  });
  const trackLearnerCreated = useTrackLearnerCreatedDuringEnrollment();
  const handleEnrollClick = React.useCallback(
    (
      newEnrollmentInputs: EnrollmentInput[],
      charterEnrollmentInfo?: EnrollmentMeetingPurchaseCharterInput,
    ) => {
      setEnrollmentInputs(newEnrollmentInputs);
      if (!!charterEnrollmentInfo && prepayType === PrepayType.CharterAdmin) {
        setCharterAdminEnrollmentInput({
          learnerName: learnerItems[0].learner?.name || "",
          name: charterEnrollmentInfo.name,
          email: charterEnrollmentInfo.email,
        });
      }

      dispatch({
        type: "ENROLL_CLICK",
        value: {
          shouldShowCoppa:
            !isCharterEnrollment &&
            shouldShowCoppa({
              // @ts-ignore TS(2322): Type '(InputMaybe<EnrollmentLearnerInput> | undefi... Remove this comment to see the full error message
              learners: _.map(newEnrollmentInputs, "learner"),
              // @ts-ignore TS(2322): Type '{ __typename?: "User" | undefined; consented... Remove this comment to see the full error message
              parent: enrollingParent,
              activity,
            }),
          prepayType,
          enrollmentCharterInput:
            isCharterEnrollment && charterEnrollmentInfo
              ? charterEnrollmentInfo
              : null,
          canPayLater: canPayLater,
          sectionUid: sectionUid,
        },
      });
    },
    [
      prepayType,
      dispatch,
      enrollingParent,
      learnerItems,
      setCharterAdminEnrollmentInput,
      setEnrollmentInputs,
      activity,
      canPayLater,
      isCharterEnrollment,
      sectionUid,
    ],
  );

  const handleFutureMeetingPageSubmit = React.useCallback(
    (duration: number) => {
      setEnrollmentDuration(duration);
      if (isPrepayEnrollment) {
        setPrepayDurationWeeks(prepayMeetingAvailability.length);
        // @ts-ignore TS(2722): Cannot invoke an object which is possibly 'undefin... Remove this comment to see the full error message
        setTeacherUid(orgTeacher?.uid);
        // @ts-ignore TS(2722): Cannot invoke an object which is possibly 'undefin... Remove this comment to see the full error message
        setOngoingStartTime(startTime);
        // @ts-ignore TS(2722): Cannot invoke an object which is possibly 'undefin... Remove this comment to see the full error message
        setOngoingEndTime(endTime);
      }

      dispatch({
        type: "FUTURE_MEETINGS_SHOWN",
      });
    },
    [
      isPrepayEnrollment,
      setEnrollmentDuration,
      setTeacherUid,
      setPrepayDurationWeeks,
      setOngoingStartTime,
      setOngoingEndTime,
      prepayMeetingAvailability,
      startTime,
      endTime,
      orgTeacher,
    ],
  );

  const handleMakeItOngoingSubmit = React.useCallback(
    ({
      duration,
      canScheduleContinuously,
      futureMeetings,
    }: {
      duration: number;
      canScheduleContinuously: boolean;
      futureMeetings: FutureMeetingInfo[];
    }) => {
      setIsContinuousSchedulingAvailable(canScheduleContinuously);
      setFutureMeetingAvailability(futureMeetings);
      handleFutureMeetingPageSubmit(duration);
    },
    [
      setIsContinuousSchedulingAvailable,
      setFutureMeetingAvailability,
      handleFutureMeetingPageSubmit,
    ],
  );

  const handleDurationPageSubmit = React.useCallback(
    ({
      duration,
      canScheduleContinuously = false,
      futureMeetings,
    }: {
      duration: number | null;
      canScheduleContinuously?: boolean;
      futureMeetings: FutureMeetingInfo[];
    }) => {
      let prepayAvailability: FutureMeetingInfo[] = [];
      if (duration) {
        prepayAvailability = futureMeetings
          .slice(0, duration)
          .filter(meeting => {
            return (
              meeting.isAvailable &&
              dayjs(meeting.startTime).diff(dayjs(), "days") <=
                MAX_DAYS_AVAILABILITY
            );
          });
      }
      const displayableFutureMeetings = isPrepayEnrollment
        ? futureMeetings.map(meeting => ({
            ...meeting,
            isAvailable:
              meeting.isAvailable &&
              dayjs(meeting.startTime).diff(dayjs(), "days") <=
                MAX_DAYS_AVAILABILITY,
          }))
        : futureMeetings;

      setEnrollmentDuration(duration);
      setFutureMeetingAvailability(displayableFutureMeetings);
      setPrepayMeetingAvailability(prepayAvailability);
      setIsContinuousSchedulingAvailable(canScheduleContinuously);

      dispatch({
        type: "MEETING_COUNT_SELECTED",
        value: {
          shouldShowFutureMeetings: duration !== 1,
        },
      });
    },
    [
      isPrepayEnrollment,
      setEnrollmentDuration,
      setFutureMeetingAvailability,
      setPrepayMeetingAvailability,
      setIsContinuousSchedulingAvailable,
    ],
  );

  const handleThreeStepAvailabilitySubmit = (
    meetingStartTime: Date,
    meetingEndTime: Date,
    meetingDurationWeeks: number | null,
    meetingTimes?: FutureMeetingInfo[],
    orgTeacher?: EnrollmentOrgTeacher,
  ) => {
    setTutorMatchEnrollmentData?.(data => {
      return {
        ...data!,
        planActivity: {
          meetingTimes: meetingTimes
            ? meetingTimes.map(meeting => ({
                meetingStartTime: meeting.startTime,
                meetingEndTime: meeting.endTime,
                isAvailable: meeting.isAvailable,
                orgTeacher,
              }))
            : [
                {
                  meetingStartTime,
                  meetingEndTime,
                  isAvailable: true,
                  orgTeacher,
                },
              ],
          meetingDurationWeeks,
        },
      };
    });
  };

  const handleCreateEnrollments = React.useCallback(async () => {
    if (isPricingBreakdownLoading) {
      return;
    }

    if (isEsaSession && prepayMeetingAvailability.length > 0) {
      const lastMeeting = _.sortBy(prepayMeetingAvailability, "startTime")[
        prepayMeetingAvailability.length - 1
      ];

      const beyondEsaEnd = isMeetingBeyondEsaEnd(lastMeeting.startTime);
      if (beyondEsaEnd.isMeetingBeyondEsaEnd) {
        dispatch({ type: "ERRORED", error: beyondEsaEnd.message! });
        return;
      }
    }

    const prepayMeetings =
      isPrepayEnrollment && prepayMeetingAvailability.length > 0
        ? prepayMeetingAvailability.map(meetingInfo => ({
            start_time: meetingInfo.startTime,
            end_time: meetingInfo.endTime,
          }))
        : undefined;

    dispatch({ type: "CREATING_ENROLLMENT_MEETING_PURCHASE" });
    try {
      const attribution = await analytics.attribution();

      // for make it ongoing, if the first date is not available we cannot use it as the enrollment meeting,
      // we need to use the first available date within the specified duration, if it exists.
      let enrollmentMeeting: FutureMeetingInfo | null = null;
      if (makeItOngoing) {
        const datesBooking = enrollmentDuration
          ? futureMeetingAvailability.slice(0, enrollmentDuration)
          : futureMeetingAvailability;
        const weeksToFirstAvailableDate = datesBooking.findIndex(
          meeting => meeting.isAvailable,
        );
        if (weeksToFirstAvailableDate !== -1) {
          enrollmentMeeting =
            futureMeetingAvailability[weeksToFirstAvailableDate];
          // the enrollment duration is used to create the correct number of recurring future meetings that we indicate to the user that they are booking.
          // therefore, we subtract the dates that are unavailable at the start of the duration (since the first date or first several weeks can be unavailable with make it ongoing)
          // to get the number of future meetings the user is actually booking.
          if (enrollmentDuration) {
            setEnrollmentDuration(
              enrollmentDuration - weeksToFirstAvailableDate,
            );
          }
        }
      }

      const currentUser = structuredClone(enrollingParent);

      const {
        data: {
          // @ts-ignore TS(2339): Property 'createEnrollmentMeetingPurchase' does no... Remove this comment to see the full error message
          createEnrollmentMeetingPurchase: {
            enrollmentMeetingPurchase,
            orderSlugId,
          },
        },
      } = await createEnrollmentMeeting({
        variables: {
          orderInput: {
            activityUid: activity.uid,
            enrollmentInputs,
            attribution,
            impressionId: (
              location?.state as undefined | { impressionId: string }
            )?.impressionId,
            currency_code: currencyCodeToWrite,
            exchange_rate: exchangeRateToWrite,
            startTime: enrollmentMeeting?.startTime ?? startTime,
            endTime: enrollmentMeeting?.endTime ?? endTime,
            ignoresTeacherAvailability: isAcceptingMeetingRequests,
            meetingRequestUids,
            orgTeacherUid: orgTeacher?.uid,
            prepayMeetings,
            charterParentInput: !!state.enrollmentCharterInput
              ? {
                  email: state.enrollmentCharterInput.email,
                  name: state.enrollmentCharterInput.name,
                }
              : undefined,
          },
          esaState,
        },
      });

      if (currentUser) {
        trackLearnerCreated(enrollmentInputs, currentUser);
      }

      if (!!meetingRequestUids) {
        const resp = await validateMeetingRequestsForPurchase({
          requestUids: meetingRequestUids,
          activityUid: activity.uid,
          enrollmentMeetingPurchaseUid: enrollmentMeetingPurchase.uid,
          sectionUid,
        });
        if (!resp.isValid) {
          const errMessage =
            resp.error ===
              ValidateMeetingRequestsForPurchaseError.MeetingRequestDeleted ||
            resp.error ===
              ValidateMeetingRequestsForPurchaseError.MeetingRequestIsNotPending
              ? t(
                  "Meeting requests are no longer available. Please message the educator.",
                )
              : t("Unable to accept meeting requests.");
          OsPlatform.captureError(new Error(errMessage), {
            tags: { ValidateMeetingRequestsForPurchaseError: resp.error },
          });

          dispatch({
            type: "ERRORED",
            error: errMessage,
          });
          return;
        }
      }

      dispatch({
        type: "ENROLLMENT_MEETING_PURCHASE_CREATED",
        value: {
          enrollmentMeetingPurchase: enrollmentMeetingPurchase,
          orderSlugId,
          // @ts-ignore TS(2532): Object is possibly 'undefined'.
          stripeCents: pricingBreakdown.stripeCents,
          // @ts-ignore TS(2532): Object is possibly 'undefined'.
          subscriptionOutstandingBalanceCredits:
            pricingBreakdown?.subscriptionOutstandingBalanceCredits,
          pricingOffer,
          paymentMethods,
          hasActiveSubscription,
          canPayLater,
          enrollmentDuration,
        },
      });
    } catch (error) {
      dispatch({ type: "ERRORED", error });
    }
  }, [
    state.enrollmentCharterInput,
    activity.uid,
    analytics,
    createEnrollmentMeeting,
    validateMeetingRequestsForPurchase,
    currencyCodeToWrite,
    dispatch,
    enrollmentInputs,
    exchangeRateToWrite,
    isPricingBreakdownLoading,
    location,
    paymentMethods,
    pricingBreakdown?.stripeCents,
    pricingBreakdown?.subscriptionOutstandingBalanceCredits,
    pricingOffer,
    startTime,
    endTime,
    isAcceptingMeetingRequests,
    meetingRequestUids,
    orgTeacher?.uid,
    makeItOngoing,
    enrollmentDuration,
    futureMeetingAvailability,
    isEsaSession,
    isMeetingBeyondEsaEnd,
    esaState,
    sectionUid,
    prepayMeetingAvailability,
    isPrepayEnrollment,
    hasActiveSubscription,
    canPayLater,
    trackLearnerCreated,
    enrollingParent,
    t,
  ]);

  const handleClassWalletPayment = React.useCallback(async () => {
    const classwalletCheckoutUrl = (orderSlugId: $TSFixMe) =>
      esaCheckoutUrl(
        orderSlugId,
        CLASSWALLET_CALLBACK_URL,
        CLASSWALLET_VENDOR_ID,
      );

    if (isPricingBreakdownLoading || !pricingBreakdown) {
      return;
    }

    try {
      if (!!pricingBreakdown.couponCents) {
        await acceptPaymentWithCoupon();
      }
      await acceptPaymentWithClassWallet(
        state.orderSlugId,
        pricingBreakdown.stripeCents, // price minus any applied coupons
      );
      if (state.needsCoppaConfirmation) {
        await updateCurrentUser({
          variables: {
            consented_to_coppa_notice_at: dayjs().utc().toDate(),
          },
        });
      }

      trackWithSchema({
        event: EventNames.NavigateToClassWalletToRequestOrder,
        data: {
          order_slug_id: state.orderSlugId,
          esa_id: esaSessionData?.id,
          esa_email: esaSessionData?.email,
          esa_username: esaSessionData?.username,
          esa_institution: esaSessionData?.institution,
          esa_state: esaSessionData?.shipping?.state,
        },
      });

      navigate(classwalletCheckoutUrl(state.orderSlugId), { replace: true });
    } catch (error) {
      dispatch({ type: "ERRORED", error });
    }
  }, [
    pricingBreakdown,
    state.needsCoppaConfirmation,
    updateCurrentUser,
    dispatch,
    state.orderSlugId,
    acceptPaymentWithCoupon,
    acceptPaymentWithClassWallet,
    isPricingBreakdownLoading,
    navigate,
    trackWithSchema,
    esaSessionData,
  ]);

  const handlePurchaseOrderPayment = React.useCallback(async () => {
    if (isPricingBreakdownLoading || !state.enrollmentCharterInput) {
      return;
    }

    const meetingRequestCount = meetingRequestUids?.length || 0;
    if (
      meetingRequestCount > 1 ||
      (!!meetingRequests && meetingRequests[0].is_recurring)
    ) {
      dispatch({
        type: "ERRORED",
        error: t`Charter POs currently only support single meeting requests`,
      });
      return;
    }

    try {
      const { charterSchool, intercomUrl, purchaseOrderId } =
        state.enrollmentCharterInput;
      await acceptPaymentWithPurchaseOrder(
        state.orderSlugId,
        // @ts-ignore TS(2532): Object is possibly 'undefined'.
        pricingBreakdown.totalCents,
        charterSchool!.uid,
        intercomUrl!,
        purchaseOrderId!,
      );
      const { enrollments, enrollmentMeetingPurchase, isFirstPurchaseByUser } =
        await settleOrder(state.orderSlugId, undefined, meetingRequestUids);

      trackEvent(ActionType.Payment.CREATE + "_COMPLETED", {
        activity_uid: activity.uid,
        user_uid: payingUser.uid,
        amount: EnrollmentPayment.totalPaidCents(enrollments[0].payments),
      });

      dispatch({
        type: "PAYMENT_BY_USER_COMPLETE",
        value: {
          enrollment: enrollments[0],
          enrollmentMeetingPurchase: enrollmentMeetingPurchase,
          isFirstPurchaseByUser,
        },
      });
    } catch (error) {
      trackEvent(ActionType.Payment.CREATE + "_FAILED", { error });
      try {
        await cancelOrder(state.orderSlugId);
        await refetchPricingBreakdown();
      } catch (cancelError) {
        OsPlatform.captureError(cancelError, {
          extra: {
            originalError: error,
            orderSlugId: state.orderSlugId,
          },
        });
      } finally {
        // Ensure we always return to create enrollment state even if cancelOrder is broken

        dispatch({ type: "ERRORED", error });
      }
    }
  }, [
    trackEvent,
    dispatch,
    settleOrder,
    activity,
    payingUser,
    state.orderSlugId,
    state.enrollmentCharterInput,
    acceptPaymentWithPurchaseOrder,
    pricingBreakdown?.totalCents,
    refetchPricingBreakdown,
    isPricingBreakdownLoading,
    meetingRequestUids,
    meetingRequests,
    cancelOrder,
    t,
  ]);

  const handleStripePaymentAccepted = React.useCallback(async () => {
    if (isPricingBreakdownLoading) {
      return;
    }
    if (
      // @ts-ignore TS(2532): Object is possibly 'undefined'.
      pricingBreakdown.stripeCents === 0 ||
      (hasActiveSubscription && pricingBreakdown?.subscriptionCredits === 0)
    ) {
      dispatch({ type: "HANDLING_FREE_PAYMENT" });
    } else {
      dispatch({ type: "HANDLING_PAYMENT" });
    }
    try {
      // @ts-ignore TS(2532): Object is possibly 'undefined'.
      if (!!pricingBreakdown.couponCents) {
        await acceptPaymentWithCoupon();
      }
      // @ts-ignore TS(2532): Object is possibly 'undefined'.
      if (!!pricingBreakdown.pricingOfferCents) {
        await acceptPaymentWithPricingOffer({
          // @ts-ignore TS(2322): Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
          pricingOfferUid: pricingOffer?.uid,
          // @ts-ignore TS(2322): Type 'number | undefined' is not assignable to typ... Remove this comment to see the full error message
          amountCents: pricingBreakdown?.pricingOfferCents,
        });
      }
      // @ts-ignore TS(2532): Object is possibly 'undefined'.
      if (!!pricingBreakdown.storedCreditCents) {
        await acceptPaymentWithStoredCredit(
          state.orderSlugId,
          // @ts-ignore TS(2532): Object is possibly 'undefined'.
          pricingBreakdown.storedCreditCents,
          currencyCodeToWrite,
          exchangeRateToWrite,
        );
      }

      if (!!pricingBreakdown?.subscriptionCredits) {
        await acceptPaymentWithSubscriptionCredit(
          // @ts-ignore TS(2345): Argument of type 'string | null' is not assignable... Remove this comment to see the full error message
          state.orderSlugId,
          // @ts-ignore TS(2532): Object is possibly 'undefined'.
          pricingBreakdown.subscriptionCredits,
          currencyCodeToWrite,
          exchangeRateToWrite,
        );
      }

      const learners = _.map(enrollmentInputs, "learner");
      if (!!pricingBreakdown?.esaMembershipCredits) {
        if (learners.length > 1) {
          throw new Error(
            "ESA Membership credits only apply to single learner orders",
          );
        }

        if (learners.length === 1 && !!learners[0]?.uid) {
          await acceptPaymentWithEsaMembershipCredit(
            // @ts-ignore TS(2345): Argument of type 'string | null' is not assignable... Remove this comment to see the full error message
            state.orderSlugId,
            // @ts-ignore TS(2532): Object is possibly 'undefined'.
            pricingBreakdown.esaMembershipCredits,
            currencyCodeToWrite,
            exchangeRateToWrite,
            learners[0]?.uid,
          );
        }
      }

      /**
       * EnrollmentMeetingPurchase records have already been created for future weeks for prepaid enrollments and the associated
       * meetings will be created in settleOrderEnrollmentMeetingPurchase for each of the EnrollmentMeetingPurchases tied to the
       * orderSlugId. We don't need to set duration weeks in settleOrderParams, which would then try to add future meetings as well.
       */
      // @ts-ignore TS(2322): Type '{ durationWeeks: number; } | undefined' is n... Remove this comment to see the full error message
      const settleOrderParams: PurchaseRecurringMeetingsInfo =
        enrollmentDuration !== 1 && !isPrepayEnrollment && !meetingRequestUids
          ? {
              durationWeeks: enrollmentDuration,
            }
          : undefined;
      const { enrollments, enrollmentMeetingPurchase, isFirstPurchaseByUser } =
        await settleOrder(
          state.orderSlugId,
          settleOrderParams,
          meetingRequestUids,
        );

      trackEvent(ActionType.Payment.CREATE + "_COMPLETED", {
        activity_uid: activity.uid,
        user_uid: payingUser.uid,
        amount: EnrollmentPayment.totalPaidCents(enrollments[0].payments),
      });

      dispatch({
        type: "PAYMENT_BY_USER_COMPLETE",
        value: {
          enrollment: enrollments[0],
          enrollmentMeetingPurchase: enrollmentMeetingPurchase,
          isFirstPurchaseByUser,
        },
      });
    } catch (error) {
      trackEvent(ActionType.Payment.CREATE + "_FAILED", { error });
      try {
        await cancelOrder(state.orderSlugId);
      } catch (cancelError) {
        OsPlatform.captureError(cancelError, {
          extra: {
            originalError: error,
            orderSlugId: state.orderSlugId,
          },
        });
      } finally {
        // Ensure we always return to create enrollment state even if cancelOrder is broken

        dispatch({ type: "ERRORED", error });
      }
    }
  }, [
    trackEvent,
    dispatch,
    settleOrder,
    activity,
    payingUser,
    enrollmentDuration,
    enrollmentInputs,
    state.orderSlugId,
    acceptPaymentWithPricingOffer,
    acceptPaymentWithStoredCredit,
    acceptPaymentWithSubscriptionCredit,
    acceptPaymentWithEsaMembershipCredit,
    acceptPaymentWithCoupon,
    pricingBreakdown?.pricingOfferCents,
    pricingBreakdown?.storedCreditCents,
    pricingBreakdown?.subscriptionCredits,
    pricingBreakdown?.esaMembershipCredits,
    pricingBreakdown?.stripeCents,
    pricingBreakdown?.couponCents,
    pricingOffer?.uid,
    isPricingBreakdownLoading,
    cancelOrder,
    currencyCodeToWrite,
    exchangeRateToWrite,
    meetingRequestUids,
    isPrepayEnrollment,
    hasActiveSubscription,
  ]);

  /**
   * This is not called for ClassWallet checkouts since the enrollment is
   * initially put into a pending state
   */
  const handlePaymentComplete = React.useCallback(async () => {
    if (
      state.step === Step.PurchaseCompletePaidByUser ||
      state.step === Step.PurchaseCompletePaidByOther
    ) {
      if (state.postPaymentCalled) {
        if (showMultipleMeetingsModal) {
          dispatch({
            type: "SHOW_SUGGESTED_WEEKLY_MEETINGS",
            value: {
              enrollmentMeetingPurchase: state.enrollmentMeetingPurchase,
            },
          });
        } else if (showCharterPurchaseComplete) {
          dispatch({
            type: "SHOW_CHARTER_PURCHASE_COMPLETE",
            value: {
              enrollmentMeetingPurchase: state.enrollmentMeetingPurchase,
            },
          });
        }
        return;
      }

      dispatch({ type: "POST_PAYMENT_CALLED" });

      try {
        if (state.needsCoppaConfirmation) {
          await updateCurrentUser({
            variables: { consented_to_coppa_notice_at: dayjs().utc().toDate() },
          });
        }
        const shouldCheckForSavedPayment =
          (!pricingBreakdown?.subscriptionOutstandingBalanceCredits ||
            pricingBreakdown.subscriptionOutstandingBalanceCredits <= 0) &&
          state.enrollmentDuration !== 1;

        onPaymentComplete({
          enrollment: state.enrollment,
          enrollmentMeetingPurchase: state.enrollmentMeetingPurchase,
          isFirstPurchaseByUser: state.isFirstPurchaseByUser,
          orderSlugId: state.orderSlugId!,
          enrollmentDuration:
            state.enrollmentDuration === null
              ? "UNTIL_I_CANCEL"
              : state.enrollmentDuration.toString(),
          shouldCheckForSavedPayment,
        });
      } catch (error) {
        OsPlatform.captureError(error);
      }

      if (showMultipleMeetingsModal) {
        dispatch({
          type: "SHOW_SUGGESTED_WEEKLY_MEETINGS",
          value: { enrollmentMeetingPurchase: state.enrollmentMeetingPurchase },
        });
      } else if (showCharterPurchaseComplete) {
        dispatch({
          type: "SHOW_CHARTER_PURCHASE_COMPLETE",
          value: {
            enrollmentMeetingPurchase: state.enrollmentMeetingPurchase,
          },
        });
      }
    }
  }, [
    updateCurrentUser,
    state,
    pricingBreakdown,
    onPaymentComplete,
    showMultipleMeetingsModal,
    showCharterPurchaseComplete,
  ]);

  const handleCheckoutFormCancel = React.useCallback(() => {
    if (isPrepayEnrollment) {
      setPrepayDurationWeeks(1);
      setCharterAdminEnrollmentInput(null);
      if (prepayType !== PrepayType.ClassWallet) {
        setLearnerItems([defaultLearnerItem()]);
      }
    }
    if (
      state.step === Step.EnrollmentMeetingPurchaseForm &&
      isAcceptingMeetingRequests
    ) {
      onClose();
    }
    if (
      state.step === Step.EnrollmentMeetingPurchaseForm ||
      state.step === Step.PaymentForm
    ) {
      setTutorMatchEnrollmentData?.({
        activity,
        planActivity: undefined,
      });
    }

    dispatch({ type: "CANCELLED", value: { isThreeStepBooking } });
  }, [
    isPrepayEnrollment,
    state.step,
    isAcceptingMeetingRequests,
    setPrepayDurationWeeks,
    setCharterAdminEnrollmentInput,
    prepayType,
    setLearnerItems,
    onClose,
    setTutorMatchEnrollmentData,
    activity,
    isThreeStepBooking,
  ]);

  const handleCheckoutFormGoBack = React.useCallback(() => {
    dispatch({ type: "GO_BACK", value: { isThreeStepBooking } });
  }, [dispatch, isThreeStepBooking]);

  const handleCheckoutFormCancelAndClose = React.useCallback(
    (e: React.SyntheticEvent, resetFields?: boolean) => {
      onClose(e, resetFields);

      dispatch({ type: "CANCELLED" });
    },
    [dispatch, onClose],
  );

  const handleRequestOutOfRangeEnrollment = React.useCallback(
    (learner: Child) => {
      dispatch({
        type: "REQUESTING_AGE_RANGE_EXCEPTION",
        value: { learner },
      });
    },
    [dispatch],
  );

  React.useEffect(() => {
    const overrideLearnerItems = !!learner
      ? [
          {
            key: learner.uid,
            checked: true,
            learner,
          },
        ]
      : [];

    setOverrideLearnerItems(overrideLearnerItems);
  }, [learner, setOverrideLearnerItems]);

  React.useEffect(() => {
    let secondMeeting: { startTime: Date; endTime: Date } | undefined;
    if (tutorMatchEnrollmentData) {
      const enrollmentDuration =
        tutorMatchEnrollmentData.planActivity?.meetingDurationWeeks;
      const availableMeetings =
        tutorMatchEnrollmentData.planActivity?.meetingTimes?.filter(
          meeting => meeting.isAvailable,
        );
      const meetingsWithinDuration = !!enrollmentDuration
        ? availableMeetings?.slice(0, enrollmentDuration)
        : availableMeetings;
      const secondTutorMeeting = meetingsWithinDuration?.[1];

      secondMeeting = secondTutorMeeting
        ? {
            startTime: secondTutorMeeting.meetingStartTime,
            endTime: secondTutorMeeting.meetingEndTime,
          }
        : undefined;
    } else {
      const availableMeetings = futureMeetingAvailability.filter(
        meeting => meeting.isAvailable,
      );
      const meetingsWithinRange = !!enrollmentDuration
        ? availableMeetings.slice(0, enrollmentDuration)
        : availableMeetings;

      secondMeeting = meetingsWithinRange[1];
    }

    if (!!secondMeeting) {
      const meetingStartTime = dayjs.tz(secondMeeting.startTime, userTimeZone);
      const meetingBillingDate = startOfIsoWeekUtc(meetingStartTime);

      setNextBillingDate(meetingBillingDate);
    }
  }, [
    enrollmentDuration,
    futureMeetingAvailability,
    tutorMatchEnrollmentData,
    userTimeZone,
  ]);

  React.useEffect(() => {
    if (
      state.step === Step.ShouldCreateEnrollmentMeetingPurchase &&
      pricingBreakdown &&
      !isPricingBreakdownLoading
    ) {
      handleCreateEnrollments();
    }
  }, [
    pricingBreakdown,
    state.step,
    handleCreateEnrollments,
    isPricingBreakdownLoading,
  ]);

  React.useEffect(() => {
    if (
      state.step === Step.PurchaseOrderPayment &&
      pricingBreakdown &&
      !isPricingBreakdownLoading
    ) {
      handlePurchaseOrderPayment();
    }
  }, [
    pricingBreakdown,
    state.step,
    state.enrollmentCharterInput,
    handlePurchaseOrderPayment,
    isPricingBreakdownLoading,
  ]);

  React.useEffect(() => {
    if (
      state.step === Step.ClassWalletPayment &&
      pricingBreakdown &&
      prepayType === PrepayType.ClassWallet
    ) {
      handleClassWalletPayment();
    }
  }, [
    pricingBreakdown,
    state.step,
    handleClassWalletPayment,
    isPricingBreakdownLoading,
    prepayType,
  ]);

  React.useEffect(() => {
    if (state.step === Step.PaymentForm && !isPricingBreakdownLoading) {
      trackCheckoutStarted({
        activityListing: { activity, leader: activity.leader },
        orderSlugId: state.orderSlugId,
        currencyCode,
        // @ts-ignore TS(2532): Object is possibly 'undefined'.
        totalCents: pricingBreakdown.totalCents,
      });
    }
  }, [
    activity,
    currencyCode,
    isPricingBreakdownLoading,
    state.orderSlugId,
    pricingBreakdown,
    state.step,
    trackCheckoutStarted,
  ]);

  React.useEffect(() => {
    if (
      (state.step === Step.PayingForFreeMeeting ||
        state.step == Step.PayingForEnrollment) &&
      pricingBreakdown &&
      !isPricingBreakdownLoading
    ) {
      handleStripePaymentAccepted();
    }
  }, [
    pricingBreakdown,
    state.step,
    handleStripePaymentAccepted,
    acceptPaymentWithPricingOffer,
    isPricingBreakdownLoading,
  ]);

  const { hasEnrolled } = useHasEnrolled();

  React.useEffect(() => {
    if (
      state.step === Step.PurchaseCompletePaidByOther ||
      state.step === Step.PurchaseCompletePaidByUser
    ) {
      const shouldCheckForSavedPayment =
        (!pricingBreakdown?.subscriptionOutstandingBalanceCredits ||
          pricingBreakdown.subscriptionOutstandingBalanceCredits <= 0) &&
        state.enrollmentDuration !== 1;

      handlePaymentComplete().then(() => {
        if (!hasEnrolled && !hasEsaMembership) {
          dispatch({
            type: "HOW_DID_YOU_HEAR_ABOUT_US",
            value: {
              ...state,
            },
          });
        } else {
          onCompleteFlow({
            enrollment: state.enrollment,
            shouldCheckForSavedPayment,
          });
        }
      });
    }
  }, [
    state.step,
    state.enrollmentDuration,
    pricingBreakdown,
    hasEsaMembership,
    onPaymentComplete,
    updateCurrentUser,
    handlePaymentComplete,
    hasEnrolled,
    state,
    onCompleteFlow,
    hasActiveSubscription,
  ]);

  React.useEffect(() => {
    if (
      state.step === Step.MeetingCountSelected &&
      isAcceptingMeetingRequests
    ) {
      dispatch({
        type: "MEETING_COUNT_SELECTED",
        value: {
          shouldShowFutureMeetings: false,
        },
      });
      // assumption: if more than one meeting request, will always be the week after startDate
      if (
        (meetingRequestUids && meetingRequestUids.length > 1) ||
        meetingRequests?.every((mr: $TSFixMe) => !!mr.is_recurring)
      ) {
        setNextBillingDate(startOfIsoWeekUtc(dayjs(startTime).add(1, "week")));
      }
    }
  }, [
    dispatch,
    state.step,
    isAcceptingMeetingRequests,
    startTime,
    meetingRequestUids,
    meetingRequests,
  ]);

  const handleClosePurchaseDuration = () => {
    const pathRegex = /\/enroll\b/;
    const newPath = window.location.pathname.replace(pathRegex, "");
    const queryParams = new URLSearchParams(window.location.search);
    navigate(`${newPath}?${queryParams.toString()}`);
  };

  return (
    <React.Fragment>
      {state.step === Step.HowDidYouHearAboutUs && (
        <Box sx={{ justifyContent: "left" }}>
          <Typography
            variant="h5"
            sx={{ margin: "10px auto", fontWeight: "bold" }}
          >
            {t("Thanks for your enrollment!")}
          </Typography>
        </Box>
      )}
      {![
        Step.RequestingAgeRangeException,
        Step.SuggestMultipleMeetings,
        Step.ShowCharterPurchaseComplete,
        Step.ThreeStepBookingAvailability,
      ].includes(state.step) && (
        <ActivitySummary
          activity={activity}
          startTime={startTime}
          endTime={endTime}
          nextOngoingMeeting={null}
          numMeetings={meetingRequestUids?.length}
          isRecurring={meetingRequests?.every(
            (mr: $TSFixMe) => !!mr.is_recurring,
          )}
          orgTeacher={orgTeacher}
        />
      )}
      {state.step === Step.ThreeStepBookingAvailability && (
        <Box>
          <ScheduleDateTimeSelector
            activity={activity}
            meetingStartTime={startTime}
            withHeader={true}
            isThreeStepBooking={isThreeStepBooking}
            orgTeacherUid={orgTeacher?.uid}
            isPrepayEnrollment={isPrepayEnrollment}
            onChange={handleThreeStepAvailabilitySubmit}
            onHandleClose={handleCheckoutFormCancelAndClose}
            activityWithEnrollableTimes={activityWithEnrollableTimes}
          />
        </Box>
      )}
      {state.step === Step.MeetingCountSelected && (
        <>
          {isPrepayEnrollment ? (
            <Box>
              <EnrollmentMeetingPurchasePrepayDuration
                startTime={startTime}
                endTime={endTime}
                activity={activity}
                onBack={handleClosePurchaseDuration}
                onClose={handleCheckoutFormCancelAndClose}
                onNextClick={handleDurationPageSubmit}
                orgTeacherUid={orgTeacher?.uid}
              />
            </Box>
          ) : (
            <Box>
              <EnrollmentMeetingPurchaseDuration
                showCloseButton={showCloseButton}
                startTime={startTime}
                endTime={endTime}
                activity={activity}
                onBack={handleClosePurchaseDuration}
                onClose={handleCheckoutFormCancelAndClose}
                onNextClick={handleDurationPageSubmit}
                orgTeacherUid={orgTeacher?.uid}
                canPayLater={canPayLater}
                showCancel={showCancel}
                resetFields={resetFields}
              />
            </Box>
          )}
        </>
      )}
      {state.step === Step.FutureMeetings && (
        <Box>
          {makeItOngoing ? (
            <EnrollmentMeetingPurchaseFutureMeetingsWrapper
              activity={activity}
              startTime={startTime}
              endTime={endTime}
              orgTeacher={orgTeacher}
              handleMakeItOngoingSubmit={handleMakeItOngoingSubmit}
              handleCheckoutFormCancelAndClose={
                handleCheckoutFormCancelAndClose
              }
              isPrepayEnrollment={isPrepayEnrollment}
            />
          ) : (
            <EnrollmentMeetingPurchaseFutureMeetings
              duration={enrollmentDuration}
              onBack={handleCheckoutFormCancel}
              onClose={handleCheckoutFormCancelAndClose}
              futureMeetings={futureMeetingAvailability}
              isContinuousSchedulingAvailable={isContinuousSchedulingAvailable}
              onNextClick={handleFutureMeetingPageSubmit}
              isPrepayEnrollment={isPrepayEnrollment}
            />
          )}
        </Box>
      )}
      {state.step === Step.EnrollmentMeetingPurchaseForm && (
        <Box>
          <EnrollmentMeetingPurchaseForm
            canPayLater={canPayLater}
            disabled={
              arePaymentMethodsLoading ||
              isCreditAccountLoading ||
              isSubscriptionAccountLoading ||
              isEsaMembershipAccountLoading ||
              isPricingBreakdownLoading
            }
            enrollmentInProgress={creationInProgress}
            disableOnSubmit={false}
            activity={activity}
            lastSubmittedEnrollments={
              (enrollingParent && enrollingParent.lastSubmittedEnrollments) ||
              []
            }
            requestAgeRangeException={handleRequestOutOfRangeEnrollment}
            ignoreStrictAgeRange={isAcceptingMeetingRequests}
            onClose={
              makeItOngoing
                ? handleCheckoutFormGoBack
                : handleCheckoutFormCancel
            }
            error={state.error}
            onEnrollClick={handleEnrollClick}
            enrollingParent={enrollingParent}
            disableLearnerSelect={!!learner}
            nextBillingDate={nextBillingDate}
            orgTeacher={orgTeacher}
            prepayType={prepayType}
            setParentByEmail={setParentByEmail}
            nextButtonText={
              hasActiveSubscription
                ? pricingBreakdown?.subscriptionOutstandingBalanceCredits
                  ? t("Pay Outstanding Balance")
                  : t("Enroll")
                : isClassWalletSession
                ? t("Enroll with ClassWallet")
                : t("Next")
            }
            enrollmentDuration={enrollmentDuration}
            isThreeStepBooking={isThreeStepBooking}
            startTime={startTime}
            endTime={endTime}
          />
        </Box>
      )}
      {!!pricingBreakdown?.totalCents &&
        sectionUid &&
        state.step === Step.CreateMeetingsWithoutPayment && (
          <Box>
            <EnrollmentMeetingsWithoutPayment
              hasActiveSubscription={hasActiveSubscription}
              onSubmit={(errors: boolean) => {
                if (errors) {
                  dispatch({
                    type: "ERRORED",
                    error: t("Unable to accept meeting requests."),
                  });
                  return;
                }
                onClose();
                refetchMeetings && refetchMeetings();
              }}
              onCancel={handleCheckoutFormCancel}
              totalCents={pricingBreakdown.totalCents}
              activity={activity}
              startTime={startTime}
              endTime={endTime}
              durationWeeks={enrollmentDuration}
              sectionUid={sectionUid}
              isRecurring={
                enrollmentDuration === null || enrollmentDuration > 1
              }
              orgTeacherUid={orgTeacher?.uid}
              meetingRequestUids={meetingRequestUids}
              recurringScheduleUid={recurringScheduleUid}
            />
          </Box>
        )}
      {[
        Step.CreatingEnrollmentMeetingPurchase,
        Step.ShouldCreateEnrollmentMeetingPurchase,
        Step.HandlingFreePayment,
        Step.PurchaseCompletePaidByUser,
        Step.PurchaseCompletePaidByOther,
      ].includes(state.step) && (
        <LoadingWithMessage>
          {t`Please wait while we process your enrollment. Please DO NOT close this \
screen or hit the back button.`}
        </LoadingWithMessage>
      )}
      {state.step === Step.RequestingAgeRangeException && (
        <RequestAgeRangeException
          activity={activity}
          learner={state.selectedLearnerForMessage}
          onCancel={handleCheckoutFormCancel}
          startTime={startTime}
          endTime={endTime}
          onSend={onClose}
        />
      )}
      {[Step.PaymentForm, Step.HandlingPayment].includes(state.step) && (
        <StripeCheckoutForm
          isPayingOutstandingCreditsBalance={
            hasActiveSubscription &&
            !!pricingBreakdown?.subscriptionOutstandingBalanceCredits &&
            pricingBreakdown.subscriptionOutstandingBalanceCredits > 0
          }
          onCancel={handleCheckoutFormCancel}
          onStripePaymentAccepted={handleStripePaymentAccepted}
          // @ts-ignore TS(2322): Type 'FullCurrentUserFragmentFragment' is not assi... Remove this comment to see the full error message
          payingUser={payingUser}
          chargeInLocalCurrency
          // @ts-ignore TS(2322): Type 'number | undefined' is not assignable to typ... Remove this comment to see the full error message
          amountCents={pricingBreakdown?.stripeCents}
          orderSlugId={state.orderSlugId}
          activity={activity}
          isSavedPaymentMethodRequired={
            !isPrepayEnrollment && !!nextBillingDate
          }
          charterSchoolUid={organizationAdmin?.organization?.uid}
          isThreeStepBooking={isThreeStepBooking}
          durationWeeks={enrollmentDuration}
        />
      )}
      {state.step === Step.SavePaymentForm && (
        <SavePaymentMethod
          isCoppaConsentCheck={true}
          formOptions={{
            title: t`Credit Card Verification`,
            description: t`You won’t be charged, but you may see a temporary $0 or $1 entry on your statement that will be removed within 3 days.`,
            buttonLabel: t`Verify Card`,
          }}
          onPaymentSaved={handleStripePaymentAccepted}
          onGoBack={handleCheckoutFormCancel}
        />
      )}
      {state.step === Step.SuggestMultipleMeetings && (
        <MultipleMeetingsModal
          activity={activity}
          payingUser={payingUser}
          startTime={startTime}
          endTime={endTime}
          durationWeeks={enrollmentDuration}
          onClose={onMultipleMeetingsModalClose}
          actualWeeklyMeetings={actualWeeklyMeetings}
        />
      )}
      {state.step === Step.ShowCharterPurchaseComplete && (
        <CharterPurchaseComplete />
      )}
      {state.step === Step.HowDidYouHearAboutUs && (
        <HowDidYouHearAboutUsModal
          handleFlowComplete={() =>
            onCompleteFlow({
              enrollment: state.enrollment,
            })
          }
        />
      )}
    </React.Fragment>
  );
}

export default function EnrollmentMeetingPurchaseWithStripeWrapper(
  props: Omit<EnrollmentMeetingPurchaseWithStripeProps, "setParentByEmail">,
) {
  const { currencyCode } = useCurrencyLocalization();
  const { exchangeRate } = useExchangeRate(currencyCode);
  const { meetingRequestUids, payingUser, prepayType } = props;
  const isAcceptingMeetingRequests =
    !!meetingRequestUids && meetingRequestUids.length > 0;
  const [parentByEmail, setParentByEmail] = React.useState<
    ParentChildrenQuery["parentByEmail"] | null
  >(null);

  let enrollingParent;
  let conflictingEnrollments;
  let pendingEnrollments;
  let enrollmentsInOtherSections;
  let lastSubmittedEnrollments;

  if (prepayType === PrepayType.CharterPO) {
    enrollingParent = parentByEmail;
  } else if (prepayType === PrepayType.CharterAdmin) {
    // Nothing to do
  } else {
    enrollingParent = props.enrollingParent;
    conflictingEnrollments = props.conflictingEnrollments;
    pendingEnrollments = props.pendingEnrollments;
    enrollmentsInOtherSections = props.enrollmentsInOtherSections;
    lastSubmittedEnrollments = enrollingParent?.lastSubmittedEnrollments;
  }
  return (
    <OrderProvider
      buyingUserUid={payingUser?.uid}
      activity={props.activity}
      // @ts-ignore
      enrollingParent={enrollingParent}
      lastSubmittedEnrollments={lastSubmittedEnrollments}
      currencyCode={currencyCode}
      exchangeRate={exchangeRate}
      isUsingPurchaseOrder={prepayType === PrepayType.CharterPO}
      ignoreStrictAgeRange={isAcceptingMeetingRequests}
      conflictingEnrollments={conflictingEnrollments}
      pendingEnrollments={pendingEnrollments}
      enrollmentsInOtherSections={enrollmentsInOtherSections}
      hasEsaMembership={payingUser?.hasEsaMembership}
    >
      <EnrollmentMeetingPurchaseWithStripe
        {...props}
        setParentByEmail={setParentByEmail}
      />
    </OrderProvider>
  );
}
